import { useEffect, useRef, useState } from 'react';
import { REFERENCE_PLACES_KIND_COLORS, REFERENCE_PLACES_PIN_STYLE } from '../styles/mapTheme';
import { waitForMapStyleReady } from '../utils/waitForMapStyleReady';
import { bindReferencePlaceImages, ensureReferencePlaceImages } from '../utils/referencePlaceIcons';
import {
  isReferencePlacesGeoJsonSource,
  placesToGeoJSON,
  moveReferencePlacesForeground,
  referencePlacesInsertBeforeLayerId,
  REFERENCE_PLACES_LAYER_CIRCLES_ID,
  REFERENCE_PLACES_LAYER_DOTS_ID,
  REFERENCE_PLACES_SOURCE_ID,
} from '../utils/referencePlaces';

function removeReferencePlacesLayers(map) {
  if (!map) return;
  try {
    if (map.getSource(REFERENCE_PLACES_SOURCE_ID) && !isReferencePlacesGeoJsonSource(map)) return;
    if (map.getLayer(REFERENCE_PLACES_LAYER_DOTS_ID)) {
      map.removeLayer(REFERENCE_PLACES_LAYER_DOTS_ID);
    }
    if (map.getLayer(REFERENCE_PLACES_LAYER_CIRCLES_ID)) {
      map.removeLayer(REFERENCE_PLACES_LAYER_CIRCLES_ID);
    }
    if (map.getSource(REFERENCE_PLACES_SOURCE_ID)) {
      map.removeSource(REFERENCE_PLACES_SOURCE_ID);
    }
  } catch (_) {
    // mapa o estilo aún no listos
  }
}

function upsertReferencePlacesLayers(map, geojson) {
  // En share de lote isStyleLoaded() queda false mientras cargan teselas;
  // addSource/addLayer GeoJSON igual pueden ir. Si fallan, styledata reintenta.

  let hasSource;
  let hasIcons;
  let hasCircles;
  try {
    bindReferencePlaceImages(map);
    hasSource = !!map.getSource(REFERENCE_PLACES_SOURCE_ID);
    hasIcons = !!map.getLayer(REFERENCE_PLACES_LAYER_DOTS_ID);
    hasCircles = !!map.getLayer(REFERENCE_PLACES_LAYER_CIRCLES_ID);
  } catch (_) {
    return { action: 'skip-style' };
  }

  const images = ensureReferencePlaceImages(map);

  if (hasSource && !isReferencePlacesGeoJsonSource(map)) {
    return { action: 'skip-vector-source' };
  }

  if (hasSource && hasIcons && hasCircles) {
    const source = map.getSource(REFERENCE_PLACES_SOURCE_ID);
    if (typeof source?.setData !== 'function') {
      return { action: 'skip-vector-source' };
    }
    source.setData(geojson);
    moveReferencePlacesForeground(map);
    return { action: 'setData', imagesAdded: images.added, imagesReady: images.ready };
  }

  if (hasSource || hasIcons || hasCircles) removeReferencePlacesLayers(map);

  try {
    const beforeId = referencePlacesInsertBeforeLayerId(map);
    map.addSource(REFERENCE_PLACES_SOURCE_ID, { type: 'geojson', data: geojson });
    map.addLayer({
      id: REFERENCE_PLACES_LAYER_CIRCLES_ID,
      type: 'circle',
      source: REFERENCE_PLACES_SOURCE_ID,
      paint: {
        'circle-radius': 6,
        'circle-color': ['coalesce', ['get', 'color'], REFERENCE_PLACES_KIND_COLORS.default],
        'circle-stroke-width': 2,
        'circle-stroke-color': REFERENCE_PLACES_PIN_STYLE.strokeColor,
        'circle-opacity': 0.95,
      },
    }, beforeId);
    map.addLayer({
      id: REFERENCE_PLACES_LAYER_DOTS_ID,
      type: 'symbol',
      source: REFERENCE_PLACES_SOURCE_ID,
      layout: {
        'icon-image': ['coalesce', ['get', 'icon'], 'ref-place-default'],
        'icon-size': REFERENCE_PLACES_PIN_STYLE.iconSize,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
        'icon-padding': 0,
      },
    }, beforeId);
    moveReferencePlacesForeground(map);
    return { action: 'addLayer', imagesAdded: images.added, imagesReady: images.ready };
  } catch (error) {
    console.error('No se pudieron pintar los puntos de interés', error);
    return { action: 'error' };
  }
}

/**
 * Capa GeoJSON de pines (fallback share anónimo: GET por caja).
 */
export function useReferencePlacesLayer({
  mapInstanceRef,
  places,
  enabled,
  kindLabels = {},
}) {
  const mapRef = useRef(null);
  const placesRef = useRef(places);
  const kindLabelsRef = useRef(kindLabels);
  const [mapAttachVersion, setMapAttachVersion] = useState(0);
  placesRef.current = places;
  kindLabelsRef.current = kindLabels;

  useEffect(() => {
    if (!enabled) return undefined;

    const map = mapInstanceRef.current;
    if (map) {
      setMapAttachVersion((version) => version + 1);
      return undefined;
    }

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      if (mapInstanceRef.current) {
        setMapAttachVersion((version) => version + 1);
        window.clearInterval(intervalId);
        return;
      }
      if (attempts >= 120) window.clearInterval(intervalId);
    }, 50);

    return () => window.clearInterval(intervalId);
  }, [enabled, mapInstanceRef]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    mapRef.current = map;

    if (!map || !enabled) {
      removeReferencePlacesLayers(map);
      return undefined;
    }

    let cancelled = false;
    const currentGeoJSON = () => placesToGeoJSON(placesRef.current, kindLabelsRef.current);
    let paintQueued = false;

    const paint = () => {
      if (cancelled) return;
      paintQueued = false;
      upsertReferencePlacesLayers(map, currentGeoJSON());
    };

    waitForMapStyleReady(map).then(() => paint());

    const handleStyleData = () => {
      if (cancelled) return;
      try {
        if (
          map.getSource(REFERENCE_PLACES_SOURCE_ID)
          && map.getLayer(REFERENCE_PLACES_LAYER_DOTS_ID)
          && map.getLayer(REFERENCE_PLACES_LAYER_CIRCLES_ID)
        ) {
          ensureReferencePlaceImages(map);
          return;
        }
      } catch (_) { /* estilo a medias */ }
      if (paintQueued) return;
      paintQueued = true;
      window.requestAnimationFrame(() => paint());
    };

    map.on('styledata', handleStyleData);

    return () => {
      cancelled = true;
      try { map.off('styledata', handleStyleData); } catch (_) { /* noop */ }
    };
  }, [enabled, mapInstanceRef, mapAttachVersion]);

  useEffect(() => {
    if (!enabled) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    upsertReferencePlacesLayers(map, placesToGeoJSON(places, kindLabels));
  }, [enabled, places, kindLabels, mapInstanceRef, mapAttachVersion]);

  useEffect(() => {
    if (enabled) return;
    removeReferencePlacesLayers(mapRef.current);
  }, [enabled]);

  useEffect(() => () => {
    removeReferencePlacesLayers(mapRef.current);
  }, []);
}

export { emptyReferencePlacesGeoJSON } from '../utils/referencePlaces';
